/**
 * Self-contained HTML embed builders for the 3 promo banners.
 *
 * Each function returns a single <a> + inline <style> snippet so the user can
 * paste it onto any third-party page (forum, blog, traffic source) and have
 * the animation work without any external CSS dependency. Class names are
 * namespaced with ``sc1cb-{size}-{slug}`` to avoid host-page collisions.
 *
 * Each embed references the official 1SimpleCycler 3.0 logo via an absolute
 * URL on the platform's custom domain so the image loads correctly from
 * any external host page. The base URL honors REACT_APP_FRONTEND_PUBLIC_URL
 * (with a safe fallback) so flipping the env on go-live propagates here
 * automatically.
 */

// Production custom domain — hardcoded so that any third-party site
// where these embeds get pasted loads the logo from the real platform
// domain (not from a preview environment).
const LOGO_BASE = 'https://1simplecycler3.com';
const LOGO_728 = `${LOGO_BASE}/logo/logo-728.png`;
const LOGO_468 = `${LOGO_BASE}/logo/logo-468.png`;
const LOGO_125 = `${LOGO_BASE}/logo/logo-125.png`;

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function buildEmbed728x90(refLink) {
  const link = esc(refLink);
  return `<!-- 1SimpleCycler 3.0 :: 728x90 Leaderboard -->
<a href="${link}" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none" aria-label="1SimpleCycler 3.0">
  <style>
    .sc1cb728{width:728px;height:90px;background:linear-gradient(180deg,#0a0a0b 0%,#000 100%);border:1px solid rgba(255,182,18,0.45);border-radius:8px;display:flex;align-items:center;padding:0 14px;font-family:'Inter','Helvetica Neue',Arial,sans-serif;color:#f7f7f8;overflow:hidden;position:relative;box-shadow:0 12px 28px -10px rgba(255,182,18,0.35)}
    .sc1cb728 *{box-sizing:border-box}
    .sc1cb728 .lg{flex-shrink:0;width:273px;height:36px;margin-right:18px;object-fit:contain;display:block;animation:sc1cb728pulse 1.5s ease-in-out infinite}
    .sc1cb728 .mq{flex:1;height:32px;overflow:hidden;mask-image:linear-gradient(90deg,transparent 0,#000 6%,#000 94%,transparent 100%);-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 6%,#000 94%,transparent 100%)}
    .sc1cb728 .mq .mqt{display:flex;gap:80px;white-space:nowrap;height:100%;align-items:center;width:max-content;animation:sc1cb728marq 16s linear infinite;font-size:12px;color:#FFE08A;font-weight:500}
    .sc1cb728 .cta{flex-shrink:0;margin-left:14px;padding:9px 14px;border-radius:8px;font-weight:800;font-size:12px;letter-spacing:0.06em;text-transform:uppercase;background:linear-gradient(180deg,#FFC20E,#FFB612);color:#0a0a0b;animation:sc1cb728flash 1.1s ease-in-out infinite alternate}
    @keyframes sc1cb728pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
    @keyframes sc1cb728marq{from{transform:translate3d(0,0,0)}to{transform:translate3d(-50%,0,0)}}
    @keyframes sc1cb728flash{0%{filter:brightness(1)}100%{filter:brightness(1.18);box-shadow:0 0 22px rgba(255,182,18,0.55)}}
  </style>
  <div class="sc1cb728">
    <img class="lg" src="${LOGO_728}" alt="1SimpleCycler 3.0" width="273" height="36" />
    <div class="mq"><div class="mqt"><span>CRITICAL UPDATE: There is absolutely NO 'NEXT IN LINE' here! Shuffled randomly every 3 seconds! Sign up today, cycle today!</span><span>CRITICAL UPDATE: There is absolutely NO 'NEXT IN LINE' here! Shuffled randomly every 3 seconds! Sign up today, cycle today!</span></div></div>
    <span class="cta">Join &amp; Cycle</span>
  </div>
</a>`;
}

export function buildEmbed468x60(refLink) {
  const link = esc(refLink);
  return `<!-- 1SimpleCycler 3.0 :: 468x60 Full Banner -->
<a href="${link}" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none" aria-label="1SimpleCycler 3.0 Join">
  <style>
    .sc1cb468{width:468px;height:60px;border-radius:8px;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:0 12px;font-family:'Inter','Helvetica Neue',Arial,sans-serif;background:linear-gradient(180deg,#0a0a0b 0%,#000 100%);border:1px solid rgba(255,182,18,0.55);box-shadow:0 12px 28px -10px rgba(255,182,18,0.35);color:#f7f7f8;position:relative;overflow:hidden;animation:sc1cb468glow 2.2s ease-in-out infinite}
    .sc1cb468 *{box-sizing:border-box}
    .sc1cb468 .lg{flex-shrink:0;width:182px;height:24px;object-fit:contain;display:block;animation:sc1cb728pulse 1.5s ease-in-out infinite}
    .sc1cb468 .tx{flex:1;text-align:center;font-weight:800;font-size:12px;letter-spacing:0.08em;color:#FFE08A;text-transform:uppercase}
    .sc1cb468 .cta{flex-shrink:0;padding:7px 12px;border-radius:6px;font-weight:800;font-size:11px;letter-spacing:0.08em;text-transform:uppercase;background:linear-gradient(180deg,#FFC20E,#FFB612);color:#0a0a0b;animation:sc1cb728flash 1.1s ease-in-out infinite alternate}
    @keyframes sc1cb728pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
    @keyframes sc1cb728flash{0%{filter:brightness(1)}100%{filter:brightness(1.18);box-shadow:0 0 14px rgba(255,182,18,0.55)}}
    @keyframes sc1cb468glow{0%,100%{box-shadow:0 12px 28px -10px rgba(255,182,18,0.35);border-color:rgba(255,182,18,0.55)}50%{box-shadow:0 14px 38px -8px rgba(255,182,18,0.65);border-color:rgba(255,224,138,0.85)}}
  </style>
  <div class="sc1cb468">
    <img class="lg" src="${LOGO_468}" alt="1SimpleCycler 3.0" width="182" height="24" />
    <span class="tx">CYCLE REAL CRYPTO</span>
    <span class="cta">JOIN &rarr;</span>
  </div>
</a>`;
}

export function buildEmbed125x125(refLink) {
  const link = esc(refLink);
  return `<!-- 1SimpleCycler 3.0 :: 125x125 Square Button -->
<a href="${link}" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none" aria-label="1SimpleCycler 3.0">
  <style>
    .sc1cb125{width:125px;height:125px;box-sizing:border-box;background:linear-gradient(180deg,#0a0a0b 0%,#000 100%);border:2px dashed #FFB612;border-radius:10px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;padding:8px 6px;text-align:center;font-family:'Inter','Helvetica Neue',Arial,sans-serif;color:#f7f7f8;animation:sc1cb125bounce 1.6s ease-in-out infinite;box-shadow:0 12px 28px -10px rgba(255,182,18,0.45)}
    .sc1cb125 *{box-sizing:border-box}
    .sc1cb125 .lg{width:106px;height:14px;object-fit:contain;margin-bottom:4px;display:block}
    .sc1cb125 .t{font-weight:800;font-size:11px;color:#FFE08A;text-transform:uppercase;letter-spacing:0.10em;line-height:1.15}
    .sc1cb125 .c{margin-top:5px;font-weight:800;font-size:10px;color:#0a0a0b;background:linear-gradient(180deg,#FFC20E,#FFB612);padding:4px 12px;border-radius:6px;text-transform:uppercase;letter-spacing:0.08em;animation:sc1cb728flash 1.1s ease-in-out infinite alternate}
    @keyframes sc1cb125bounce{0%,100%{transform:translate3d(0,0,0)}50%{transform:translate3d(0,-6px,0)}}
    @keyframes sc1cb728flash{0%{filter:brightness(1)}100%{filter:brightness(1.18);box-shadow:0 0 12px rgba(255,182,18,0.55)}}
  </style>
  <div class="sc1cb125">
    <img class="lg" src="${LOGO_125}" alt="1SimpleCycler 3.0" width="106" height="14" />
    <span class="t">CYCLE</span>
    <span class="t">REAL</span>
    <span class="t">CRYPTO</span>
    <span class="c">JOIN &rarr;</span>
  </div>
</a>`;
}
